import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, AlertCircle, Loader2, Shield, Users, Calendar, BarChart3, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LoginPage - Event Owner/Staff Login
 * Supports both Google OAuth and Email/Password login
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(location.state?.error || null);
  const [loginMode, setLoginMode] = useState('options'); // 'options', 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // Skip if returning from OAuth (has session_id in hash)
      if (window.location.hash?.includes('session_id=')) {
        setChecking(false);
        return;
      }

      try {
        const response = await fetch(`${API}/api/auth/me`, {
          credentials: 'include'
        });

        if (response.ok) {
          // Already authenticated, redirect to dashboard
          navigate('/admin/dashboard', { replace: true });
          return;
        }
      } catch (err) {
        // Not authenticated, show login
      }
      setChecking(false);
    };

    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store user info in localStorage for persistence
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
      }
      
      // Successfully logged in - use window.location for full page reload
      // This ensures cookies are properly read on the new page
      window.location.href = '/admin/dashboard';
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-600 to-green-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl font-bold">M</span>
            </div>
            <span className="text-2xl font-bold">Michezonet</span>
          </Link>
        </div>
        
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Event Management Dashboard
            </h1>
            <p className="text-green-100 text-lg">
              Create, manage, and grow your events with powerful tools designed for African sports and entertainment.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <FeatureCard icon={Calendar} title="Event Creation" description="Build events in minutes" />
            <FeatureCard icon={Users} title="Attendee Management" description="Track registrations" />
            <FeatureCard icon={Shield} title="Secure Payments" description="M-Pesa & Bank transfers" />
            <FeatureCard icon={BarChart3} title="Real-time Analytics" description="Live insights" />
          </div>
        </div>
        
        <div className="relative z-10 text-green-100 text-sm">
          &copy; {new Date().getFullYear()} Michezonet. All rights reserved.
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 text-white">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl font-bold">M</span>
              </div>
              <span className="text-2xl font-bold">Michezonet</span>
            </Link>
          </div>
          
          <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Welcome Back</h2>
              <p className="text-gray-400">Sign in to manage your events</p>
            </div>
            
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {loginMode === 'options' ? (
              <>
                {/* Google Login */}
                <Button
                  onClick={handleGoogleLogin}
                  className="w-full h-12 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl flex items-center justify-center gap-3 mb-4"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
                
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-gray-800 text-gray-400">or</span>
                  </div>
                </div>
                
                {/* Email/Password Login */}
                <Button
                  onClick={() => setLoginMode('password')}
                  variant="outline"
                  className="w-full h-12 border-gray-600 text-white hover:bg-gray-700 rounded-xl flex items-center justify-center gap-3"
                  data-testid="email-login-btn"
                >
                  <Mail className="w-5 h-5" />
                  Sign in with Email
                </Button>
              </>
            ) : (
              /* Email/Password Form */
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="you@example.com"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl pl-10 pr-12 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your password"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl"
                  data-testid="submit-login-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
                
                <button
                  type="button"
                  onClick={() => setLoginMode('options')}
                  className="w-full text-center text-sm text-gray-400 hover:text-white mt-4"
                >
                  ← Back to login options
                </button>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Want to organize events?{' '}
                <Link to="/become-organizer" className="text-green-500 hover:text-green-400">
                  Apply here
                </Link>
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Link to="/" className="text-gray-400 hover:text-white text-sm">
              &larr; Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Feature card component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
    <Icon className="w-6 h-6 text-white mb-2" />
    <h3 className="font-medium text-white">{title}</h3>
    <p className="text-sm text-green-100">{description}</p>
  </div>
);

export default LoginPage;
